import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { useQueryInvalidation } from '@pkg/hooks';
import { getNextPageParam } from '@pkg/hooks/deprecated';
import { capitalizeFirstLetter } from '@pkg/utils';

import { type CollectionChainType, Wishlists } from '@app/client';
import {
    type CreateWishlistParam,
    type DeleteWishlistParam,
    type EditWishlistParam,
    type FollowWishlistParam,
    type UseAddToWishlistParam,
    type WishlistItemPostParam,
} from '@app/client/common/structs/wishlists/wishlists';
import { WISHLIST_SUMMARY_CACHE } from '@app/client/resources/fantasy';
import {
    callGetAllWishlists,
    callGetCurrentUserWishlists,
    WISHLIST_QUERY_CACHES,
    WISHLISTS_DATA,
} from '@app/client/resources/wishlists';

export enum WishlistPathValue {
    myWishlist = 'my-wishlists',
    following = 'following',
    popular = 'popular',
}

const CACHES = [...WISHLIST_QUERY_CACHES, ...WISHLIST_SUMMARY_CACHE];
export const FOLLOW_CACHE = ['following=true', 'public=true', WISHLISTS_DATA, 'lists'];

export const useWishlistData = (slug?: string) => {
    const cache = [WISHLISTS_DATA, slug];
    return useInfiniteQuery({
        queryKey: cache,
        queryFn: ({ pageParam = 0, signal }) =>
            callGetCurrentUserWishlists({
                slug,
                query: { page: pageParam || 0, limit: 10 },
                settings: { signal },
            }),
        retry: false,
        staleTime: Infinity,
        placeholderData: keepPreviousData,
        initialPageParam: 0,
        getNextPageParam,
    });
};

export const useWishlistsData = (pathQuery: Record<string, boolean | undefined>, disabled?: boolean, limit = 10) => {
    const pathQueryCache = Object.keys(pathQuery).map((k) => `${k}=${pathQuery[k]}`);
    const cache = [...pathQueryCache, WISHLISTS_DATA, 'lists', limit];

    return useInfiniteQuery({
        queryKey: cache,
        queryFn: ({ pageParam = 0, signal }) =>
            callGetAllWishlists({
                query: { page: pageParam || 0, limit, ...pathQuery },
                settings: { signal },
            }),
        retry: false,
        initialPageParam: 0,
        getNextPageParam,
        enabled: !disabled,
    });
};

export function useCreateWishlist() {
    const invalidateWishlists = useQueryInvalidation(WISHLIST_QUERY_CACHES);
    return useMutation({
        mutationFn: ({ name, isPrivate = false }: CreateWishlistParam) =>
            Wishlists.callCreateUserWishlist({ name, isPrivate }),
        onSuccess: invalidateWishlists,
    });
}

export function useUpdateWishlist() {
    const invalidateWishlists = useQueryInvalidation(WISHLIST_QUERY_CACHES);
    return useMutation({
        mutationFn: ({ wishlist_id, name, isPrivate }: EditWishlistParam) =>
            Wishlists.callUpdateUserWishlist({ wishlist_id, name, isPrivate }),
        onSuccess: invalidateWishlists,
    });
}

export function useDeleteWishlist() {
    const invalidateWishlists = useQueryInvalidation(WISHLIST_QUERY_CACHES);
    return useMutation({
        mutationFn: ({ wishlist_id }: DeleteWishlistParam) => Wishlists.callDeleteUserWishlist({ wishlist_id }),
        onSuccess: invalidateWishlists,
    });
}

interface CreateWishlistParamValueProps {
    contract?: string;
    token_id?: string;
    name?: string;
    slug?: string;
    network?: string | CollectionChainType;
}

export function createWishlistParamValue({
    contract,
    token_id,
    name,
    network: networkArg,
    slug,
}: CreateWishlistParamValueProps): WishlistItemPostParam {
    const network = capitalizeFirstLetter(networkArg || '') as CollectionChainType;
    if (contract && token_id) return { Token: { contract, token_id }, network };
    if (name && slug) return { Trait: { slug, name }, network };
    if (slug) return { Collection: { slug }, network };

    return { Collection: { slug: 'error-not-found' }, network };
}

export function useAddToWishlist() {
    const invalidateWishlists = useQueryInvalidation(CACHES);
    return useMutation({
        mutationFn: ({ wishlistId, ...args }: UseAddToWishlistParam) => {
            const value = createWishlistParamValue({ ...args });
            return Wishlists.callAddToUserWishlist({
                wishlistId,
                value,
                network: capitalizeFirstLetter(args.network || '') as CollectionChainType,
            });
        },
        onSuccess: invalidateWishlists,
    });
}

export function useDeleteFromWishlist(props?: { preventQueryInvalidate?: boolean }) {
    const invalidateQueries = useQueryInvalidation(CACHES);
    const { preventQueryInvalidate = false } = props || {};
    const handleInvalidate = () => !preventQueryInvalidate && invalidateQueries;
    return useMutation({
        mutationFn: ({ wishlistId, ...args }: UseAddToWishlistParam) => {
            const value = createWishlistParamValue({ ...args });
            return Wishlists.callDeleteFromUserWishlist({ wishlistId, value });
        },
        onSuccess: handleInvalidate,
        onError: handleInvalidate,
    });
}

interface UseItemInWishlistsParams {
    contract?: string; // Needed for adding NFT Asset (token)
    token_id?: string; // Needed for adding NFT Asset (token)
    slug?: string; // Needed for adding a Collection
    disabled?: boolean; // Needed for adding a Collection
    chain?: string | CollectionChainType;
}

export const useItemInWishlists = ({ contract, token_id, slug, disabled, chain }: UseItemInWishlistsParams) => {
    const isAsset = Boolean(contract && token_id);
    const isCollection = Boolean(slug);

    let cache = ['item-wishlists'];
    let key: string | undefined;

    if (contract && token_id && chain) {
        cache = ['item-wishlists-token', contract, token_id, chain];
        key = JSON.stringify({ Token: { contract, token_id } });
    } else if (slug && chain) {
        cache = ['item-wishlists-collection', slug, chain];
        key = JSON.stringify({ Collection: { slug } });
    }

    const encodedKey = key ? encodeURI(key) : '';
    const chainArg = capitalizeFirstLetter(chain || '');
    const { data: publicCall } = useQuery(
        // TODO de-dup this
        // if not public call the counts are based on current user from BE :(
        {
            queryKey: [...cache, 'public-count-call'],
            queryFn: ({ signal }) =>
                Wishlists.callWishlistCount({
                    key: encodedKey,
                    network: chainArg,
                    settings: {
                        signal,
                    },
                }),
            retry: false,
            enabled: (isAsset || isCollection) && !disabled,
            select: (res) => {
                if (res?.ok && res.value) {
                    return res.value;
                }
            },
        },
    );

    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            Wishlists.callItemInWishlists({
                key: encodedKey,
                network: chainArg,
                settings: {
                    signal,
                },
            }),
        retry: false,
        enabled: (isAsset || isCollection) && !disabled,
        select: (res) => {
            if (res?.ok && res.value) {
                return res.value.map((item) => {
                    const matched = publicCall?.find((_item) => {
                        if (isCollection) {
                            return item.item.Collection?.slug === _item.item.Collection?.slug;
                        }
                        if (isAsset) {
                            return (
                                item.item.Token?.token_id === _item.item?.Token?.token_id &&
                                item.item.Token?.contract === _item.item?.Token?.contract
                            );
                        }
                    });
                    return { ...item, counts: matched?.counts ?? item.counts };
                });
            }
        },
    });
};

export function useFollowWishlist() {
    return useMutation({
        mutationFn: ({ slug }: FollowWishlistParam) => Wishlists.callFollowWishlist({ slug }),
    });
}

export function useUnfollowWishlist() {
    return useMutation({
        mutationFn: ({ slug }: FollowWishlistParam) => Wishlists.callUnfollowWishlist({ slug }),
    });
}
